import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ProgressBar } from './ProgressBar';
import { toast, useToastStore, type ToastItem, type ToastTone } from './toast';
import styles from './Toast.module.css';

const ICONS: Record<ToastTone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

function ToastView({ item }: { item: ToastItem }) {
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (item.duration === null || hovered) return;
    const timer = window.setTimeout(() => toast.dismiss(item.id), item.duration);
    return () => window.clearTimeout(timer);
  }, [item.id, item.duration, item.revision, hovered]);

  const Icon = ICONS[item.tone];
  return (
    <div
      className={clsx(styles.toast, styles[item.tone])}
      role={item.tone === 'error' ? 'alert' : 'status'}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <Icon className={styles.icon} aria-hidden />
      <div className={styles.content}>
        <p className={styles.title}>{item.title}</p>
        {item.description && <p className={styles.description}>{item.description}</p>}
        {item.progress !== undefined && (
          <ProgressBar value={item.progress} label={item.title} size="sm" className={styles.progress} />
        )}
        {item.action && (
          <button
            type="button"
            className={styles.action}
            onClick={() => {
              item.action?.onClick();
              toast.dismiss(item.id);
            }}
          >
            {item.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notification"
        onClick={() => toast.dismiss(item.id)}
      >
        <X aria-hidden />
      </button>
    </div>
  );
}

/** Toast viewport (bottom-right). Mount once near the app root. */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  return createPortal(
    <section className={styles.viewport} aria-label="Notifications" aria-live="polite">
      {toasts.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </section>,
    document.body,
  );
}
